import { useApi } from '@/api';
import { ProgressBar } from '@/components';
import type { ProgressBarProps } from '@/components/ProgressBar';
import { useChartTooltip } from '@/hooks';
import { determineAssetColor } from '@/utils';
import {
  Card,
  CurrencyValue,
  DoughnutChart,
  Flex,
  TableSkeleton,
  Text,
  formatPercentage,
  hexToRgb,
  Heading,
  useIsMobile
} from '@blockanalitica/ui';
import { useMemo } from 'react';
import { useParams } from 'react-router';
import { useTheme } from 'styled-components';

type AllocationData = {
  name: string;
  symbol: string;
  amount_usd: string;
  percentage: string;
};

interface ProgressData extends ProgressBarProps {
  text: string;
  percent: number;
}

export default function AllocationTableBreakdown({
  symbol
}: {
  symbol: string;
}) {
  const isMobile = useIsMobile();
  const theme = useTheme();
  const { network, address, tokenAddress } = useParams();
  const [data, error] = useApi<AllocationData[]>({
    url: `/sparkstar/sll/tokens/${network}/${address}/${tokenAddress}/allocation/`
  });

  const processedData: ProgressData[] | null = useMemo(() => {
    if (!data || data.length === 0) {
      return null;
    }
    // Sort data by percentage in descending order
    const sortedData = [...data].sort(
      (a, b) => Number(b.percentage) - Number(a.percentage)
    );

    // Split into top 5 and others and transform them
    const topFive = sortedData.slice(0, 5).map((item) => ({
      text: item.name,
      percent: Number(item.percentage),
      additionalValue: (
        <CurrencyValue value={item.amount_usd} options={{ currency: 'USD' }} />
      ),
      color: determineAssetColor(item.symbol)
    }));
    const others = sortedData.slice(5);

    // Calculate total percentage/amount for others
    const othersPercentage = others.reduce(
      (sum, item) => sum + Number(item.percentage),
      0
    );
    const othersAmount = others.reduce(
      (sum, item) => sum + Number(item.amount_usd),
      0
    );
    // Return top 5 plus Others if there are more than 5 items
    if (others.length > 0) {
      return [
        ...topFive,
        {
          text: 'Others',
          percent: othersPercentage,
          additionalValue: (
            <CurrencyValue value={othersAmount} options={{ currency: 'USD' }} />
          ),
          color: theme.colors['progressBarOthers'],
          breakdown: others.map((item) => ({
            text: item.name,
            percent: item.percentage,
            additionalValue: (
              <CurrencyValue
                value={item.amount_usd}
                options={{ currency: 'USD' }}
              />
            )
          }))
        }
      ];
    }

    return topFive;
  }, [data, theme.colors]);

  const customTooltip = useChartTooltip(
    processedData?.map(({ text, percent, additionalValue }) => ({
      text,
      percent,
      additionalValue
    })),
    'text',
    'percent',
    'additionalValue'
  );
  const customConfig = useMemo(
    () => ({
      options: {
        layout: {
          padding: 10
        },
        plugins: {
          legend: {
            position: 'bottom'
          },
          tooltip: {
            enabled: false,
            position: 'nearest' as const,
            external: customTooltip
          }
        }
      }
    }),
    [customTooltip]
  );

  const chartData = useMemo(() => {
    if (!processedData) {
      return;
    }

    const labels: string[] = [];
    const backgroundColor: string[] = [];
    const colorsBorder: string[] = [];
    const datasetData: number[] = [];

    processedData.forEach((row) => {
      const { r, g, b } = hexToRgb(row.color);
      labels.push(row.text);
      backgroundColor.push(`rgba(${r}, ${g}, ${b}, 0.5)`);
      colorsBorder.push(`rgba(${r}, ${g}, ${b}, 1)`);
      datasetData.push(row.percent);
    });

    return {
      labels,
      backgroundColor,
      colorsBorder,
      datasets: [
        {
          data: datasetData,
          hoverOffset: 15
        }
      ]
    };
  }, [processedData]);

  if (error) {
    throw error;
  }
  if (!data) {
    // TODO: wrong skeleton
    return <TableSkeleton rows={10} columns={6} />;
  }

  if (!processedData || !chartData) {
    return null;
  }

  return (
    <Card flexDirection="column" gap="xs">
      <Heading size="4">Allocation Breakdown</Heading>

      <Text color="muted" fontSize="-1" paddingBottom="s">
        {symbol} is supplied to different markets, where it becomes available
        for users to borrow.
      </Text>

      <Flex gap="s">
        {!isMobile ? (
          <Card flexDirection="column" gap="m" backgroundColor="foreground">
            {/* 
              DoughnutChart needs to be wrapped in Flex (technically div) in order
              for the custom tooltip to attach to the correct parent
            */}
            <Flex>
              <DoughnutChart
                config={customConfig}
                data={chartData}
                valueFormatter={formatPercentage}
              />
            </Flex>
          </Card>
        ) : null}
        <Flex flexDirection="column" gap="s" flex="1">
          {processedData.map((row) => (
            <ProgressBar key={row.text} {...row} />
          ))}
        </Flex>
      </Flex>
    </Card>
  );
}
